import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from '../../modules/auth/auth.service';

export interface AppConfig {
	inputStyle: string;
	colorScheme: string;
	theme: string;
	ripple: boolean;
	menuMode: string;
	scale: number;
}

interface LayoutState {
	staticMenuDesktopInactive: boolean;
	overlayMenuActive: boolean;
	profileSidebarVisible: boolean;
	configSidebarVisible: boolean;
	staticMenuMobileActive: boolean;
	menuHoverActive: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class LayoutService {
	_config: AppConfig = {
		ripple: false,
		inputStyle: 'outlined',
		menuMode: 'static',
		colorScheme: 'light',
		theme: 'lara-light-indigo',
		scale: 14
	};

	config: WritableSignal<AppConfig> = signal<AppConfig>(this._config);

	state: LayoutState = {
		staticMenuDesktopInactive: false,
		overlayMenuActive: false,
		profileSidebarVisible: false,
		configSidebarVisible: false,
		staticMenuMobileActive: false,
		menuHoverActive: false
	};

	private configUpdate = new Subject<AppConfig>();

	private overlayOpen = new Subject<any>();

	private showLoginModal = new Subject<boolean>();

	private showLogin: boolean = false;

	private spinnerStatus = new BehaviorSubject<boolean>(false);

	private menuToggle = new Subject<void>();

	configUpdate$ = this.configUpdate.asObservable();

	overlayOpen$ = this.overlayOpen.asObservable();

	showLoginModal$ = this.showLoginModal.asObservable();

	showSpinner$ = this.spinnerStatus.asObservable();

	menuToggle$ = this.menuToggle.asObservable();

	private authService = inject(AuthService);

	private get sidebarStorageKey(): string {
		const userId = this.authService.getLoggedInUser()?.userId;
		return userId ? `dme_sidebar_${userId}` : 'dme_sidebar_guest';
	}

	constructor() {
		const saved = localStorage.getItem(this.sidebarStorageKey);
		this.state.staticMenuDesktopInactive = saved !== null ? saved === 'true' : false;
	}

	onMenuToggle(persist: boolean = true) {
		if (this.isOverlay()) {
			this.state.overlayMenuActive = !this.state.overlayMenuActive;
			if (this.state.overlayMenuActive) {
				this.overlayOpen.next(null);
			}
		}

		if (this.isDesktop()) {
			this.state.staticMenuDesktopInactive = !this.state.staticMenuDesktopInactive;
			if (persist) {
				localStorage.setItem(this.sidebarStorageKey, String(this.state.staticMenuDesktopInactive));
			}
		} else {
			this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

			if (this.state.staticMenuMobileActive) {
				this.overlayOpen.next(null);
			}
		}

		// Emit menu toggle event for all cases (desktop, mobile, overlay)
		this.menuToggle.next();
	}

	showProfileSidebar() {
		this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
		if (this.state.profileSidebarVisible) {
			this.overlayOpen.next(null);
		}
	}

	onLogInClicked() {
		this.showLogin = !this.showLogin;
		this.showLoginModal.next(this.showLogin);
	}

	onShowSpinner() {
		this.spinnerStatus.next(true);
	}

	onHideSpinner() {
		this.spinnerStatus.next(false);
	}

	isOverlay() {
		return this.config().menuMode === 'overlay';
	}

	isDesktop() {
		return window.innerWidth > 991;
	}

	isMobile() {
		return !this.isDesktop();
	}
}
